/* eslint-disable jsx-a11y/label-has-associated-control */
import { memo, useRef, useCallback, type ComponentType, type ReactNode } from 'react';
import { RadioGroup, Radio } from 'components/inputs/RadioGroup';
import get from 'lodash/get';
import ScreenLink from 'components/common/ScreenLink';
import type { IScreenLinkData } from 'components/common/ScreenLink/ScreenLink';
import { areEqualFields, scrollToNextMatchingSibling } from 'utils/form';
import { useIntl, type IntlShape } from 'react-intl';
import type { TFieldComponentProps, TFormValues } from './types';
import s from './InlineSelect.module.scss';

type TInlineSelectValue = string | number | boolean;

/** An image import: its URL, or a module whose `default` is the URL. */
type TImageSource = string | { default: string };

export interface IInlineSelectOption {
  /** The image, rendered when there is no `imageSrc`. */
  img: ComponentType<{ title?: string }>;
  imageSrc?: TImageSource;
  label: string;
  description?: ReactNode;
  value: string;
}

interface IRadioItemProps extends IInlineSelectOption {
  name: string;
  t: IntlShape['formatMessage'];
  index: number;
}

export interface IInlineSelectChange {
  value: TInlineSelectValue;
  form: TFieldComponentProps<unknown, TFormValues>['form'];
}

export interface IInlineSelectProps extends TFieldComponentProps<
  TInlineSelectValue | null | undefined
> {
  options: IInlineSelectOption[];
  label?: ReactNode;
  sideEffectOnChange?: (change: IInlineSelectChange) => void;
  moreDetails?: IScreenLinkData;
  skipScrollingToField?: boolean;
}

const imageUrlOf = (imageSrc: TImageSource) => {
  if (typeof imageSrc === 'string') return imageSrc;
  return imageSrc.default;
};

function RadioItem({
  imageSrc,
  img: Img,
  label,
  description,
  value,
  name,
  t,
  index,
}: IRadioItemProps) {
  return (
    <div className={s.radioItem} style={description ? { alignSelf: 'flex-start' } : undefined}>
      <div className={s.wrapper}>
        <label className={s.imageLabel} htmlFor={`${name}.${index}`}>
          {/* added ?.default according to this issue https://github.com/facebook/create-react-app/issues/9992 */}
          {imageSrc ? (
            <img
              src={imageUrlOf(imageSrc)}
              alt={t({ id: 'alt_text.btn_select_with_icon' }, { btnLabel: label })}
            />
          ) : (
            <Img title={t({ id: 'alt_text.btn_select_with_icon' }, { btnLabel: label })} />
          )}
          <div className={s.container}>
            <div className={s.round}>
              <Radio type="radio" id={`${name}.${index}`} name={name} value={value} />
              <label htmlFor={`${name}.${index}`} />
            </div>
          </div>
        </label>
      </div>
      <div className="ml-4">
        <label className={s.optionLabel}>{label}</label>
        {description ? (
          <div className="text-left">
            <small className="muted">{description}</small>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const InlineSelect = ({
  form,
  options,
  field,
  label,
  sideEffectOnChange,
  moreDetails,
  skipScrollingToField,
}: IInlineSelectProps) => {
  const { formatMessage: t } = useIntl();
  const { errors, touched } = form;
  const { name, value } = field;
  const fieldWrapperRef = useRef<HTMLDivElement>(null);

  const handleChange = useCallback(
    (v: TInlineSelectValue) => {
      form.setFieldValue(name, v);
      if (typeof sideEffectOnChange === 'function') sideEffectOnChange({ value: v, form });
      if (v) scrollToNextMatchingSibling(fieldWrapperRef);
    },
    [form, name, sideEffectOnChange],
  );

  return (
    <div ref={fieldWrapperRef} className={!skipScrollingToField ? 'scroll-to-field' : ''}>
      <label className={s.inlineSelectLabel}>
        {label} {moreDetails && <ScreenLink linkData={moreDetails} />}
      </label>
      <RadioGroup
        className={s.radioGroup}
        name={name}
        selectedValue={value}
        onChange={handleChange}
      >
        {options.map((option, index) => (
          <RadioItem key={option.value} {...{ ...option, name, t, index }} />
        ))}
      </RadioGroup>

      {get(errors, name) && get(touched, name) && (
        <div className={s.errorSelect}>{get(errors, name)}</div>
      )}
    </div>
  );
};

export default memo(InlineSelect, (prevProps, nextProps) =>
  areEqualFields(prevProps, nextProps, ['options']),
);
